import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";

import { ApiError, api } from "../api/client";
import { Button, Card, EmptyState, PageHeader, Skeleton } from "../components/ui";

/**
 * A printable receipt.
 *
 * Rendered from the API's receipt payload so the copy on paper is the same
 * record the register holds -- no client-side arithmetic, no reconstruction.
 * `window.print()` is the whole mechanism; a receipt is a page, not a PDF
 * pipeline.
 */
export function ReceiptPage() {
  const { id } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: ["receipt", id],
    queryFn: ({ signal }) => api.paymentReceipt(id!, signal),
    enabled: Boolean(id),
  });

  return (
    <>
      <div className="print:hidden">
        <Link
          to="/billing/payments"
          className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
        >
          <ArrowLeft size={14} /> All payments
        </Link>
        <PageHeader
          title="Receipt"
          actions={
            <Button variant="primary" onClick={() => window.print()} disabled={!query.data}>
              <Printer size={14} /> Print
            </Button>
          }
        />
      </div>

      {query.isPending ? (
        <Card>
          <Skeleton className="h-64 w-full" />
        </Card>
      ) : query.isError || !query.data ? (
        <Card>
          <EmptyState
            title="Receipt not found"
            body={
              query.error instanceof ApiError
                ? query.error.message
                : "This payment may have been removed."
            }
            action={
              <Link to="/billing/payments" className="text-sm font-medium underline">
                Back to payments
              </Link>
            }
          />
        </Card>
      ) : (
        <Card className="mx-auto max-w-2xl">
          <div className="flex items-start justify-between border-b border-slate-200 pb-4">
            <div>
              <h1 className="text-lg font-semibold text-slate-900">
                {query.data.account_name}
              </h1>
              <p className="text-sm text-slate-500">Payment receipt</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-slate-500">Receipt</p>
              <p className="text-sm font-medium tabular-nums text-slate-900">
                {query.data.receipt_number}
              </p>
            </div>
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-y-3 text-sm">
            <Row label="Customer" value={query.data.customer_name || "—"} />
            <Row label="Phone" value={query.data.customer_phone || "—"} />
            <Row label="Invoice" value={query.data.invoice_number || "—"} />
            <Row label="Date" value={query.data.payment_date} />
            <Row label="Method" value={query.data.method} />
            <Row label="Reference" value={query.data.reference || "—"} />
            <Row label="Issued by" value={query.data.issued_by || "—"} />
            <Row
              label="Direction"
              value={query.data.direction === "outbound" ? "Refund" : "Received"}
            />
          </dl>

          <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-4">
            <span className="text-sm font-medium text-slate-500">
              {query.data.direction === "outbound" ? "Amount refunded" : "Amount received"}
            </span>
            <span className="text-xl font-semibold tabular-nums text-slate-900">
              {query.data.currency} {query.data.amount}
            </span>
          </div>

          {query.data.notes ? (
            <p className="mt-4 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
              {query.data.notes}
            </p>
          ) : null}

          <p className="mt-6 text-center text-xs text-slate-400">
            Thank you. This is a computer-generated receipt.
          </p>
        </Card>
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right text-slate-800">{value}</dd>
    </>
  );
}
